"use client"

import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Box, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, Typography } from "@mui/material"
import { NavLink, useNavigate } from "react-router-dom"
import Grid from "@mui/material/Grid"
import { getsinglebookingdata, isBookingAccessDenied } from "../../store/BookingHistory/BookingHistorySlice"
import { UNAUTHORIZED_BOOKING_REDIRECT_STATE } from "../common/UnauthorizedBookingModal"
import backarrow from "../../assets/images/back-arrow.svg"
import airline from "../../assets/images/active-flight-icon.svg"
import { Skeleton } from "antd"
import { getqueryId } from "../../store/Queries/QueriesSlice"
import TextField from "@mui/material/TextField"
import moment from "moment"
import { generateBookingHTML, formatBookingDataForHTML } from "../Invoice/booking-html-generator"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import { useSearchParams } from "react-router-dom"
import Bookingintermediatrypage from "../common/PaymentSection/BookingIntermediatrypage"
import { updatequeryid } from "../../store/Newsearchform/Newsearchform"
import { authuser } from "../../store/Authentication/AuthSlice"

const Newaircargodetail = ({ type, markup_detail }) => {
  const [open, setOpen] = useState(false)
  const [nodeData, setNodeData] = useState({})
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const role = useSelector(authuser)
  const [isDownloading, setIsDownloading] = useState(false)
  // Removed downloadMethod state - using HTML-based PDF only

  const queryid = searchParams.get("queryId")

  const queryId = useSelector(getqueryId) || queryid
  const stickyHeader = useRef()
  const dispatch = useDispatch()

  const bookingDetail = nodeData && Object.keys(nodeData).length !== 0 ? nodeData?.bookeditem : ""
  const searchquery = nodeData && Object.keys(nodeData).length !== 0 ? nodeData?.searchquery : ""



  const handleOpenChange = (newOpen) => {
    setOpen(newOpen)
  }

  const closePopover = () => {
    setOpen("")
  }

  const navigate = useNavigate()

  useEffect(() => {
    if (queryId) {
      dispatch(getsinglebookingdata({ queryId }))
        .then((res) => {
          const currentUserRole = role?.userroles
          const isKamOrAdmin = currentUserRole === "kam" || currentUserRole === "air_admin"

          // single_booking_data answers a queryId belonging to another account with
          // 404 "Booking not found" and the thunk returns that as a FULFILLED
          // payload, so it never reaches .catch(). Without this test the page just
          // rendered an empty booking view — the ?type= guard in PageLayout is only
          // a link hint, so this backend 404 is the real authorisation check. Same
          // handling as the sibling detail pages (OceanCargoDetails,
          // BookingDetailPage). Only for the standalone route (no `type`
          // prop): when embedded, the host page owns navigation.
          if (!type && isBookingAccessDenied(res?.payload)) {
            // /booking-history isn't in the kam/air_admin ACL — sending them there
            // renders blank, so KAM-side sessions go to their own queries list.
            // The customer path carries the state that raises the "not authorised"
            // popup on the bookings list.
            navigate(
              isKamOrAdmin ? "/QueriesKAM" : "/booking-history",
              isKamOrAdmin
                ? { replace: true }
                : { replace: true, state: UNAUTHORIZED_BOOKING_REDIRECT_STATE }
            )
            return
          }
          const data = res?.payload?.data?.data
          setNodeData(data)
          setLoading(false)
          if (data?.booking_status === "Confirmation Awaited" && isKamOrAdmin) {
            dispatch(updatequeryid(queryId))
            setShowBookingModal(true)
          }
        })
        .catch((err) => {
          console.error(err)
        })
    }
  }, [queryId])

  // Main download handler (HTML-based PDF only)
  const handleDownload = useCallback(async () => {
    if (!nodeData || Object.keys(nodeData).length === 0) {
      console.error("No booking data available")
      return
    }

    setIsDownloading(true)
    try {
      // Format the booking data for HTML generation
      const formattedData = formatBookingDataForHTML(nodeData, role?.userroles)

      // Generate HTML content
      const htmlContent = generateBookingHTML(formattedData)

      // Create a temporary container for the HTML
      const tempContainer = document.createElement("div")
      tempContainer.innerHTML = htmlContent
      tempContainer.style.position = "absolute"
      tempContainer.style.left = "-9999px"
      tempContainer.style.top = "-9999px"
      tempContainer.style.width = "1200px"

      // Add CSS for page break handling
      const style = document.createElement("style")
      style.textContent = `
      .page-break-avoid {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .page-break-before {
        page-break-before: always !important;
        break-before: page !important;
      }
      .page-break-after {
        page-break-after: always !important;
        break-after: page !important;
      }
      .section {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        margin-bottom: 20px;
      }
      .table-container {
        page-break-inside: avoid !important;
        break-inside: avoid !important;
        margin-bottom: 20px;
      }
      @media print {
        .page-break-avoid {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .section {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .table-container {
          page-break-inside: avoid !important;
        }
      }
    `

      tempContainer.appendChild(style)

      // Append to body temporarily
      document.body.appendChild(tempContainer)

      

      // Generate canvas from HTML with better options for page breaks
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        width: 1200,
        height: tempContainer.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false,
        onclone: (clonedDoc) => {
          // Ensure styles are applied to cloned document
          const clonedStyle = clonedDoc.createElement("style")
          clonedStyle.textContent = style.textContent
          clonedDoc.head.appendChild(clonedStyle)
        },
      })

      // Remove temporary container
      document.body.removeChild(tempContainer)

      // Convert canvas to image
      const imgData = canvas.toDataURL("image/png")

      // Create PDF with better page handling
      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      // Calculate image dimensions to fit PDF
      const imgProps = pdf.getImageProperties(imgData)
      const imgWidth = pdfWidth
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width

      let heightLeft = imgHeight
      let position = 0

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
      heightLeft -= pdfHeight

      // Add additional pages if needed
      while (heightLeft > 0) {
        position -= pdfHeight
        pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
        heightLeft -= pdfHeight
      }

      // Generate filename with booking details
      const filename = `booking-${nodeData?.airWaybillNumber || "details"}-${moment().format("YYYY-MM-DD")}.pdf`

      // Save the PDF
      pdf.save(filename)
    } catch (error) {
      console.error("Error generating PDF:", error)
      alert("Error generating PDF. Please try again.")
    } finally {
      setIsDownloading(false)
    }
  }, [nodeData, role?.userroles])

  // Bookings created from a manual quote have no flightItineraries at all, so the
  // array can be missing even once bookeditem is populated — read through it
  // defensively instead of indexing directly.
  const flightItineraries = nodeData?.bookeditem?.flightItineraries
  const firstItinerary = flightItineraries?.[0]
  const lastItinerary = flightItineraries?.[flightItineraries.length - 1]

  const gridItems = [
    { label: "Origin", value: nodeData?.bookeditem?.origin?nodeData?.bookeditem?.origin:"N/A" },
    { label: "Destination", value: nodeData?.bookeditem?.destination?nodeData?.bookeditem?.destination:"N/A" },
    {
      label: "Chargeable Weight",
      value: nodeData?.bookeditem?.rateDetails?.chargeableWeight?nodeData?.bookeditem?.rateDetails?.chargeableWeight:"N/A",
    },
    {
      label: "Commodity",
      value: nodeData?.search_query_data?.airlinepayloadData?.commodityDetails,
    },
    {
      label: "Departure Date(origin)",
      value: firstItinerary?.scheduledDepartureDate ? firstItinerary.scheduledDepartureDate : "N/A",
    },
    {
      label: "Arrival Date(destination)",
      value: lastItinerary?.scheduledArrivalDate ? lastItinerary.scheduledArrivalDate : "N/A",
    },
    { label: "Current Status", value: nodeData?.booking_status?nodeData?.booking_status:"N/A" },
    { label: "AWB Number", value: nodeData?.airWaybillNumber?nodeData?.airWaybillNumber:"N/A" },
    {
      label: "Booking Date",
      value: moment(nodeData?.booking_date).format("YYYY-MM-DD")?moment(nodeData?.booking_date).format("YYYY-MM-DD"):"N/A",
    },
  ]

  const gridItems2 = [
    {
      label: "Air Freight Rate/kg",
      value: nodeData?.bookeditem?.rateDetails?.ratePerkilo ? nodeData?.bookeditem?.rateDetails?.ratePerkilo : 'N/A',
    },
    {
      label: "Air Freight Rate/Total ",
      value: nodeData?.bookeditem?.rateDetails?.totalAmount ? nodeData?.bookeditem?.rateDetails?.totalAmount : 'N/A',
    },
    {
      label: "Origin transportation Charges",
      value: nodeData?.viewBreakupCharge?.pickupCharges ? nodeData?.viewBreakupCharge?.pickupCharges : 'N/A',
    },
    {
      label: " Destination Delivery Charges",
      value: nodeData?.viewBreakupCharge?.deliveryCharges ? nodeData?.viewBreakupCharge?.deliveryCharges : 'N/A',
    },
    {
      label: "Other Origin Charges",
      value: nodeData?.viewBreakupCharge?.OriginCharges ? nodeData?.viewBreakupCharge?.OriginCharges : 'N/A',
    },
    {
      label: "Other Destination Charges",
      value: nodeData?.viewBreakupCharge?.DestinationCharges ? nodeData?.viewBreakupCharge?.DestinationCharges : 'N/A',
    },
  ]

  const gridItems3 = [
    {
      label: "Air Freight Rate/kg",
      value: nodeData?.bookeditem?.rateDetails?.ratePerkilo?nodeData?.bookeditem?.rateDetails?.ratePerkilo:'N/A',
    },
    {
      label: "Air Freight Rate/Total ",
      value: nodeData?.bookeditem?.rateDetails?.totalAmount?nodeData?.bookeditem?.rateDetails?.totalAmount:'N/A',
    },
    {
      label: "Origin transportation Charges",
      value: nodeData?.viewBreakupCharge?.pickupCharges?nodeData?.viewBreakupCharge?.pickupCharges:'N/A',
    },
    {
      label: " Destination Delivery Charges",
      value: nodeData?.viewBreakupCharge?.deliveryCharges?nodeData?.viewBreakupCharge?.deliveryCharges:'N/A',
    },
    {
      label: "Other Origin Charges",
      value: nodeData?.viewBreakupCharge?.OriginCharges?nodeData?.viewBreakupCharge?.OriginCharges:'N/A',
    },
    {
      label: "Other Destination Charges",
      value: nodeData?.viewBreakupCharge?.DestinationCharges?nodeData?.viewBreakupCharge?.DestinationCharges:'N/A',
    },
    {
      label: "Buy/kg",
      value: nodeData?.buyRate?.rateperkillo?nodeData?.buyRate?.rateperkillo:'N/A',
    },
    {
      label: "Markup %",
      value: nodeData?.markupResult?.markup?nodeData?.markupResult?.markup:'N/A',
    },
    {
      label: "Markup value per/kg",
      value: nodeData?.markupResult?.markupRatePerKg?nodeData?.markupResult?.markupRatePerKg:'N/A',
    },
  ]

  const gridItems4 = [
    {
      label: "Minimum Markup %",
      value: nodeData?.markupResult?.minimum_markup_percentage?nodeData?.markupResult?.minimum_markup_percentage:'N/A',
    },
    {
      label: "Customer Category",
      value: nodeData?.odcc_details?.customer_category?nodeData?.odcc_details?.customer_category:'N/A',
    },
    {
      label: "ODCC Strategy",
      value: nodeData?.odcc_details?._id?nodeData?.odcc_details?._id:'N/A',
    },
    {
      label: "ODCC %",
      value: nodeData?.odcc_details?.markup_percentage?nodeData?.odcc_details?.markup_percentage:'N/A',
    },
  ]

  useLayoutEffect(() => {
    const mainHeader = document.getElementById("mainHeader")
    if (mainHeader) {
      const fixedTop = stickyHeader?.current?.offsetTop
      const fixedHeader = () => {
        if (window.pageYOffset > fixedTop) {
          mainHeader.classList.add("fixed")
          mainHeader.classList.add("top-0")
        } else {
          mainHeader.classList.remove("fixed")
          mainHeader.classList.remove("top-0")
        }
      }
      window.addEventListener("scroll", fixedHeader)
    }
  }, [])

  console.log("data", nodeData, nodeData?.bookeditem?.productCommodityDetails?.natureOfGoods)

  if (showBookingModal && nodeData?.bookeditem) {
    return (
      <Bookingintermediatrypage
        isOpen={true}
        pageMode={true}
        selectedItem={nodeData.bookeditem}
        nodeData1={nodeData}
        props={{
          requestType: nodeData?.search_query_data?.airlinepayloadData?.requestType || "1",
          queryId: queryId,
        }}
        requestType={nodeData?.search_query_data?.airlinepayloadData?.requestType || "1"}
        canEditAwb={true}
        redirectPath="/QueriesKAM"
        onClose={() => setShowBookingModal(false)}
        onConfirm={() => setShowBookingModal(false)}
      />
    )
  }

  return (
    <>
      <style>{`
        .page-break-avoid {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        .page-break-before {
          page-break-before: always !important;
          break-before: page !important;
        }
        .page-break-after {
          page-break-after: always !important;
          break-after: page !important;
        }
        .grid-section {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 20px;
        }
        .table-section {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
          margin-bottom: 20px;
        }
        @media print {
          .page-break-avoid {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .grid-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .table-section {
            page-break-inside: avoid !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <Skeleton active loading={loading}>
        <div className="grid grid-cols-12 gap-4 relative">
          <div className="sm:col-span-12 md:col-span-12 lg:col-span-12 col-span-12 relative">
            {type === "kam_admin" || type === "user_customer" ? (
              <></>
            ) : (
              <div
                className="flex p-5 justify-start items-center flex-wrap gap-5 w-full rounded-2xl shadow-cardsection bg-white z-10 no-print"
                id="mainHeader"
              >
                <div className="flex items-center justify-between w-full">
                  <div className=" flex gap-4 font-bold text-lg leading-5">
                    <NavLink
                      to={
                        role.userroles === "air_admin" || role.userroles === "kam" ? "/QueriesKAM" : "/booking-history"
                      }
                      className="flex items-center justify-center"
                    >
                      <img src={backarrow || "/placeholder.svg"} alt="Back Arrow" />
                    </NavLink>
                    <div>Booking Details</div>
                  </div>

                  {/* Download Options */}
                  <div className="flex items-center gap-3">
                    {/* Download Button */}
                    <button
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className="no-print flex items-center justify-center gap-2 rounded-lg bg-primary hover:bg-darkGrey text-white px-4 py-2 text-sm font-medium shadow-md hover:bg-primary/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDownloading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          Download PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="ml-auto"></div>
              </div>
            )}

            {/* Rest of your existing JSX remains the same */}
            <div className="w-full rounded-2xl shadow-cardsection bg-white my-5" id="pdf-wrapper">
              <div
                id="redirect"
                className="flex items-center gap-5 bg-[#FFE6CC] rounded-t-2xl px-10 py-5 sm:p-5 page-break-avoid"
              >
                <div className="rounded-lg w-14 h-14 flex items-center justify-center bg-white p-1 shrink-0 shadow-sm">
                  <img src={airline || "/placeholder.svg"} alt="Arrow Swap" className="w-7 h-7" />
                </div>
                <div className="flex flex-col gap-1 w-full">
                  <p className="text-base font-bold text-gray-800">
                    {firstItinerary?.carrierName} &nbsp;|&nbsp;{" "}
                    <span className="text-orange-700">{nodeData?.bookeditem?.origin} → {nodeData?.bookeditem?.destination}</span>
                  </p>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600 font-medium">
                    {nodeData?.bookeditem?.airWaybillNumber && (
                      <span className="bg-white/70 rounded px-2 py-0.5">AWB: {nodeData.bookeditem.airWaybillNumber}</span>
                    )}
                    <span className="bg-white/70 rounded px-2 py-0.5">
                      {searchquery?.packagingDetails?.shipmentTotal?.totalweight} {searchquery?.packagingDetails?.shipmentTotal?.weightunit}
                    </span>
                    <span className="bg-white/70 rounded px-2 py-0.5">
                      {searchquery?.packagingDetails?.shipmentTotal?.totalvolume} {searchquery?.packagingDetails?.shipmentTotal?.unit}
                    </span>
                    <span
                      className="rounded px-2 py-0.5 font-semibold"
                      style={{
                        backgroundColor:
                          nodeData?.booking_status === "Confirmed"
                            ? "#d1fae5"
                            : nodeData?.booking_status === "Confirmation Awaited"
                            ? "#fef3c7"
                            : "#fee2e2",
                        color:
                          nodeData?.booking_status === "Confirmed"
                            ? "#065f46"
                            : nodeData?.booking_status === "Confirmation Awaited"
                            ? "#92400e"
                            : "#991b1b",
                      }}
                    >
                      {nodeData?.booking_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Basic Details Grid Section */}
              <div className="grid-section">
                <Paper elevation={2} sx={{ p: 4 }} className="page-break-avoid">
                  <Typography variant="h6" component="div" sx={{ mb: 3, fontWeight: 600 }}>
                    Booking Details
                  </Typography>
                  <Grid container spacing={3}>
                    {gridItems.map((item, index) => (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Typography
                          variant="subtitle1"
                          component="label"
                          sx={{ mb: 1, display: "block", fontWeight: 500 }}
                        >
                          {item.label}
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={item.value || ""}
                          InputProps={{
                            readOnly: true,
                          }}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              "&:hover fieldset": {
                                borderColor: "primary.main",
                              },
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                            },
                            "& .MuiOutlinedInput-input": {
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 12px",
                              lineHeight: "1.4",
                              textAlign: "left",
                              minHeight: "20px",
                            },
                            marginBottom: "16px",
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </div>

              {/* Rate Details Grid Section */}
              <div className="grid-section">
                <Paper elevation={2} sx={{ p: 4 }} className="page-break-avoid">
                  <Typography variant="h6" component="div" sx={{ mb: 3, fontWeight: 600 }}>
                    Rate Details
                  </Typography>
                  <Grid container spacing={3}>
                    {(role?.userroles === "kam" || role?.userroles === "air_admin" ? gridItems3 : gridItems2).map(
                      (item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Typography
                            variant="subtitle1"
                            component="label"
                            sx={{ mb: 1, display: "block", fontWeight: 500 }}
                          >
                            {item.label}
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={item.value || ""}
                            InputProps={{
                              readOnly: true,
                            }}
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                minHeight: "40px",
                                display: "flex",
                                alignItems: "center",
                              },
                              "& .MuiOutlinedInput-input": {
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 12px",
                                lineHeight: "1.4",
                                textAlign: "left",
                                minHeight: "20px",
                              },
                              marginBottom: "16px",
                            }}
                          />
                        </Grid>
                      ),
                    )}
                  </Grid>
                </Paper>
              </div>

              {/* Charge Breakup Section */}
              {nodeData?.bookeditem?.rateDetails?.chargesBreakDown?.length > 0 && (
                <div className="grid-section">
                  <Paper elevation={2} sx={{ p: 4 }} className="page-break-avoid">
                    <Typography variant="h6" component="div" sx={{ mb: 3, fontWeight: 600 }}>
                      Charge Breakup
                    </Typography>
                    <TableContainer>
                      <Table
                        sx={{
                          border: "1px solid #e0e0e0",
                          "& td, & th": { border: "1px solid #e0e0e0" },
                          minWidth: 400,
                        }}
                      >
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#FFF3E0" }}>
                            <TableCell sx={{ fontWeight: 700, width: "35%" }}>Charge Type</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: "45%" }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 700, width: "20%", textAlign: "right" }}>
                              Amount ({nodeData?.bookeditem?.rateDetails?.currencyCode || "INR"})
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const charges = nodeData.bookeditem.rateDetails.chargesBreakDown
                            const grouped = charges.reduce((acc, c) => {
                              if (!acc[c.chargeType]) acc[c.chargeType] = []
                              acc[c.chargeType].push(c)
                              return acc
                            }, {})
                            const typeColors = {
                              "Freight Charges": "#E3F2FD",
                              "Origin Charges": "#E8F5E9",
                              "Destination Charges": "#FFF8E1",
                            }
                            let rows = []
                            Object.entries(grouped).forEach(([type, items]) => {
                              const subtotal = items.reduce((s, i) => s + (i.chargeAmount || 0), 0)
                              rows.push(
                                <TableRow key={`header-${type}`} sx={{ backgroundColor: typeColors[type] || "#F5F5F5" }}>
                                  <TableCell colSpan={2} sx={{ fontWeight: 600, color: "#333" }}>
                                    {type}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, textAlign: "right", color: "#333" }}>
                                    ₹{subtotal.toLocaleString("en-IN")}
                                  </TableCell>
                                </TableRow>,
                              )
                              items.forEach((charge, idx) => {
                                rows.push(
                                  <TableRow key={`${type}-${idx}`} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                                    <TableCell sx={{ pl: 3, color: "#555", fontSize: "0.875rem" }}>—</TableCell>
                                    <TableCell sx={{ color: "#555", fontSize: "0.875rem" }}>
                                      {charge.chargeDescription || charge.chargeType}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "right", color: "#555", fontSize: "0.875rem" }}>
                                      ₹{(charge.chargeAmount || 0).toLocaleString("en-IN")}
                                    </TableCell>
                                  </TableRow>,
                                )
                              })
                            })
                            const grandTotal = charges.reduce((s, c) => s + (c.chargeAmount || 0), 0)
                            rows.push(
                              <TableRow
                                key="grand-total"
                                sx={{ backgroundColor: "#FF8C00", "& td": { color: "#fff", fontWeight: 700 } }}
                              >
                                <TableCell colSpan={2} sx={{ fontSize: "0.95rem" }}>
                                  Grand Total
                                </TableCell>
                                <TableCell sx={{ textAlign: "right", fontSize: "0.95rem" }}>
                                  ₹{grandTotal.toLocaleString("en-IN")}
                                </TableCell>
                              </TableRow>,
                            )
                            return rows
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </div>
              )}

              {/* ODCC Details Grid Section */}
              {(role?.userroles === "kam" || role?.userroles === "air_admin") && (
                <div className="grid-section">
                  <Paper elevation={2} sx={{ p: 4 }} className="page-break-avoid">
                    <Typography variant="h6" component="div" sx={{ mb: 3, fontWeight: 600 }}>
                      ODCC Details
                    </Typography>
                    <Grid container spacing={3}>
                      {gridItems4.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Typography
                            variant="subtitle1"
                            component="label"
                            sx={{ mb: 1, display: "block", fontWeight: 500 }}
                          >
                            {item.label}
                          </Typography>
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={item.value || ""}
                            InputProps={{
                              readOnly: true,
                            }}
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                minHeight: "40px",
                                display: "flex",
                                alignItems: "center",
                              },
                              "& .MuiOutlinedInput-input": {
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 12px",
                                lineHeight: "1.4",
                                textAlign: "left",
                                minHeight: "20px",
                              },
                              marginBottom: "16px",
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </div>
              )}

              {/* Flight Details Table Section */}
              <div className="table-section">
                <TableContainer
                  component={Paper}
                  sx={{ marginRight: 2, marginTop: 4, border: "1px solid #ccc" }}
                  className="page-break-avoid"
                >
                  <Typography variant="h6" component="div" sx={{ padding: 2 }}>
                    Flight Details
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 4, mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        ml: 2,
                      }}
                    >
                      <Typography variant="h6" component="div" sx={{ minWidth: 40 }}>
                        LAT:
                      </Typography>
                      <TextField
                        variant="outlined"
                  value={nodeData?.bookeditem?.lat !== null ? nodeData?.bookeditem?.lat : "N/A"}
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "primary.main",
                            },
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiOutlinedInput-input": {
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                            lineHeight: "1.4",
                            textAlign: "left",
                            minHeight: "20px",
                          },
                          marginBottom: "16px",
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="h6" component="div" sx={{ minWidth: 40 }}>
                        TAO:
                      </Typography>
                      <TextField
                        variant="outlined"
                        value={nodeData?.bookeditem?.tao!==null ? nodeData?.bookeditem?.tao : "N/A"}
                        InputProps={{
                          readOnly: true,
                        }}
                        size="small"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: "primary.main",
                            },
                            minHeight: "40px",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& .MuiOutlinedInput-input": {
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                            lineHeight: "1.4",
                            textAlign: "left",
                            minHeight: "20px",
                          },
                          marginBottom: "16px",
                        }}
                      />
                    </Box>
                  </Box>
                  <Table
                    sx={{
                      border: "1px solid #ccc",
                      "& td, & th": {
                        border: "1px solid #ccc",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>S.NO</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Carrier Number</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Scheduled Arrival Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Scheduled Departure Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Segment of Departure</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Segment of Arrival</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Carrier Aircraft Type</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookingDetail?.flightItineraries?.map((flight, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{flight.carrierNumber}</TableCell>
                          <TableCell>{moment(flight.scheduledArrivalDate).format("DD-MM-YYYY")}</TableCell>
                          <TableCell>{moment(flight.scheduledDepartureDate).format("DD-MM-YYYY")}</TableCell>
                          <TableCell>{flight.segmentOfDeparture}</TableCell>
                          <TableCell>{flight.segmentOfArrival}</TableCell>
                          <TableCell>{flight.carrierAircraftType}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              {/* Shipment Dimensions Table Section */}
              <div className="table-section">
                <TableContainer
                  component={Paper}
                  sx={{ marginRight: 2, marginTop: 4, border: "1px solid #ccc" }}
                  className="page-break-avoid"
                >
                  <Typography variant="h6" component="div" sx={{ padding: 2 }}>
                    Shipment Dimensions
                  </Typography>
                  <Table
                    sx={{
                      border: "1px solid #ccc",
                      "& td, & th": {
                        border: "1px solid #ccc",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong>S.NO</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Number of Pieces</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Length</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Width</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Height</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Dimensions Unit</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Weight per Piece</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Weight Unit</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Turnable</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Stackable</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchquery?.packagingDetails?.packagingType?.packages.map((shipment, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{shipment?.units}</TableCell>
                          <TableCell>{shipment?.length}</TableCell>
                          <TableCell>{shipment?.width}</TableCell>
                          <TableCell>{shipment?.height}</TableCell>
                          <TableCell>{shipment?.unit}</TableCell>
                          <TableCell>{shipment?.weight}</TableCell>
                          <TableCell>{shipment?.weightunit}</TableCell>
                          <TableCell>{shipment?.turnable ? "Yes" : "No"}</TableCell>
                          <TableCell>{shipment?.stackable ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </div>
          </div>
        </div>
      </Skeleton>

    </>
  )
}

export default React.memo(Newaircargodetail)


